import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import config from '../config';

const LoginPage: React.FC = () => {
  const { user, login, register } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationAvailable, setRegistrationAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    login: '',
    username: '',
    email: '',
    password: ''
  });

  // Check if registration is available
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/auth/registration-status`);
        setRegistrationAvailable(response.data.registrationAvailable);
      } catch (error) {
        console.error('Failed to check registration status:', error);
      }
    };

    checkRegistrationStatus();
  }, []);

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isRegistering) {
        await register({
          username: formData.username,
          email: formData.email,
          password: formData.password
        });
      } else {
        await login({
          login: formData.login,
          password: formData.password
        });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card shadow">
              <div className="card-header bg-primary text-white text-center">
                <h4>
                  <i className="bi bi-database-gear me-2"></i>
                  Client Data Manager
                </h4>
                <p className="mb-0">
                  {isRegistering ? 'Create Admin Account' : 'Admin Login'}
                </p>
              </div>
              <div className="card-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {isRegistering ? (
                    <>
                      <div className="mb-3">
                        <label htmlFor="username" className="form-label">Username</label>
                        <input
                          type="text"
                          className="form-control"
                          id="username"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          minLength={6}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label htmlFor="login" className="form-label">Username or Email</label>
                        <input
                          type="text"
                          className="form-control"
                          id="login"
                          name="login"
                          value={formData.login}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </>
                  )}

                  <button
                    type="submit"
                    className={`btn w-100 ${isRegistering ? 'btn-success' : 'btn-primary'}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {isRegistering ? 'Creating Account...' : 'Signing In...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi bi-${isRegistering ? 'person-plus' : 'box-arrow-in-right'} me-2`}></i>
                        {isRegistering ? 'Create Account' : 'Sign In'}
                      </>
                    )}
                  </button>
                </form>

                {registrationAvailable && (
                  <div className="mt-3 text-center">
                    <small className="text-muted">
                      {isRegistering ? (
                        <>
                          Already have an account?{' '}
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => {
                              setIsRegistering(false);
                              setError('');
                            }}
                          >
                            Sign in here
                          </button>
                        </>
                      ) : (
                        <>
                          First time?{' '}
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => {
                              setIsRegistering(true);
                              setError('');
                            }}
                          >
                            Create admin account
                          </button>
                        </>
                      )}
                    </small>
                  </div>
                )}

                {!registrationAvailable && !isRegistering && (
                  <div className="mt-3 text-center">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Registration is closed. Contact an admin to create your account.
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mt-3">
              <small className="text-muted">
                <i className="bi bi-shield-check me-1"></i>
                Secure admin panel for client data management
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 